import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, X, MapPin } from 'lucide-react';
const NAMES = ['Tomás G.', 'Martín P.', 'Lucía F.', 'Sofía R.', 'Mateo L.', 'Valentina S.', 'Joaquín B.', 'Camila A.', 'Nicolás D.', 'Agustín M.', 'Florencia V.', 'Diego H.', 'Julieta O.', 'Federico C.', 'Rocío T.', 'Ignacio K.', 'Micaela N.', 'Santiago Q.', 'Brenda E.', 'Gonzalo I.', 'Carolina U.', 'Emiliano Z.', 'Antonella J.', 'Maximiliano R.'];
const CITIES = ['Buenos Aires', 'Córdoba', 'Rosario', 'Mendoza', 'La Plata', 'San Miguel de Tucumán', 'Mar del Plata', 'Salta', 'Santa Fe', 'San Juan', 'Resistencia', 'Neuquén', 'Bahía Blanca', 'Corrientes', 'Posadas', 'Paraná', 'Bariloche', 'Río Cuarto', 'Comodoro Rivadavia', 'San Luis'];
const OFFERS = [{
  name: 'PAQUETE COMPLETO PRO ÉLITE™',
  price: '$27.990',
  weight: 3 // more likely
}, {
  name: 'KIT PROFESIONAL STANDARD',
  price: '$19.990',
  weight: 2
}];
function pickRandom<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}
function pickWeightedOffer() {
  const total = OFFERS.reduce((acc, o) => acc + o.weight, 0);
  let r = Math.random() * total;
  for (const offer of OFFERS) {
    r -= offer.weight;
    if (r <= 0) return offer;
  }
  return OFFERS[0];
}
interface PurchaseData {
  id: number;
  name: string;
  city: string;
  offerName: string;
  offerPrice: string;
  minutesAgo: number;
}
function generatePurchase(id: number): PurchaseData {
  const offer = pickWeightedOffer();
  return {
    id,
    name: pickRandom(NAMES),
    city: pickRandom(CITIES),
    offerName: offer.name,
    offerPrice: offer.price,
    minutesAgo: Math.floor(Math.random() * 14) + 2 // 2-15 min ago
  };
}
export function PurchaseNotification() {
  const [current, setCurrent] = useState<PurchaseData | null>(null);
  const idRef = useRef(0);
  const hideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nextTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    function showNext() {
      idRef.current += 1;
      setCurrent(generatePurchase(idRef.current));
      // Visible for ~5.5s
      hideTimerRef.current = setTimeout(() => {
        setCurrent(null);
        // Next notification in 40-60s
        const nextDelay = 40000 + Math.random() * 20000;
        nextTimerRef.current = setTimeout(showNext, nextDelay);
      }, 5500);
    }
    // First popup after 8-12s
    const initialDelay = 8000 + Math.random() * 4000;
    nextTimerRef.current = setTimeout(showNext, initialDelay);
    return () => {
      if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
      if (nextTimerRef.current) clearTimeout(nextTimerRef.current);
    };
  }, []);
  const handleClose = () => {
    setCurrent(null);
    if (hideTimerRef.current) clearTimeout(hideTimerRef.current);
  };
  return <div className="fixed bottom-4 left-4 right-4 sm:right-auto sm:left-6 sm:bottom-6 z-50 pointer-events-none" data-id="element-292">
      <AnimatePresence mode="wait" data-id="element-293">
        {current && <motion.div key={current.id} initial={{
        opacity: 0,
        x: -40,
        y: 20,
        scale: 0.95
      }} animate={{
        opacity: 1,
        x: 0,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        x: -40,
        scale: 0.95
      }} transition={{
        duration: 0.4,
        ease: [0.16, 1, 0.3, 1]
      }} className="pointer-events-auto bg-white shadow-2xl border border-gray-200 rounded-xl sm:rounded-2xl p-3 sm:p-4 max-w-sm w-full sm:w-auto sm:min-w-[340px] flex items-start gap-3 relative" role="status" aria-live="polite" data-id="element-294">
            {/* Green icon */}
            <div className="flex-shrink-0 w-10 h-10 sm:w-11 sm:h-11 bg-green-100 rounded-full flex items-center justify-center" data-id="element-295">
              <CheckCircle2 className="w-6 h-6 sm:w-7 sm:h-7 text-green-600" strokeWidth={2.5} data-id="element-296" />
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0 pr-5" data-id="element-297">
              <div className="flex items-center gap-1.5 mb-1" data-id="element-298">
                <span className="text-[10px] sm:text-xs font-bold text-green-700 uppercase tracking-wider" data-id="element-299">
                  ✓ Compra verificada
                </span>
                <span className="inline-flex w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" data-id="element-300"></span>
              </div>

              <p className="text-sm sm:text-[15px] font-bold text-brand-dark leading-tight" data-id="element-301">
                {current.name}
              </p>

              <p className="text-[11px] sm:text-xs text-gray-500 flex items-center gap-1 mt-0.5" data-id="element-302">
                <MapPin className="w-3 h-3" data-id="element-303" /> {current.city}, Argentina
              </p>

              <p className="text-[11px] sm:text-xs text-gray-700 mt-1.5 leading-snug" data-id="element-304">
                Compró el{' '}
                <span className="font-bold text-brand-dark" data-id="element-305">
                  {current.offerName}
                </span>
              </p>

              <p className="text-[10px] sm:text-[11px] text-gray-400 mt-1" data-id="element-306">
                hace {current.minutesAgo} minuto
                {current.minutesAgo === 1 ? '' : 's'}
              </p>
            </div>

            {/* Close button */}
            <button onClick={handleClose} aria-label="Cerrar notificación" className="absolute top-2 right-2 w-6 h-6 rounded-full text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center transition-colors" data-id="element-307">
              <X className="w-3.5 h-3.5" data-id="element-308" />
            </button>

            {/* Progress bar */}
            <motion.div initial={{
          scaleX: 1
        }} animate={{
          scaleX: 0
        }} transition={{
          duration: 5.5,
          ease: 'linear'
        }} className="absolute bottom-0 left-0 right-0 h-1 bg-brand-accent origin-left rounded-b-xl sm:rounded-b-2xl" data-id="element-309" />
          </motion.div>}
      </AnimatePresence>
    </div>;
}